import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trackStripeError, trackSupabaseError } from '@/lib/analytics/errorHandler';
import { getCurrentLocale } from '@/lib/i18n';
import { logger } from '@/lib/logger';
import { trackEvent } from '@/lib/posthog';
import { trackBeginCheckout } from '@/lib/gtm';
import { googleAdsConversions } from '@/lib/googleAdsConversions';
import type { PricingVariant } from './usePricingExperiment';

export type Tier = 'free' | 'core' | 'premium' | 'professional' | 'archive';
export type SubscriptionStatus = 'active' | 'trialing' | 'expired' | 'cancelled' | 'refunded' | 'archive' | 'past_due';

export interface Subscription {
  id: string;
  user_id: string;
  tier: Tier;
  status: SubscriptionStatus;
  features: string[];
  // Strategy regenerations
  regenerations_used: number;
  regenerations_limit: number | null;
  // Document uploads
  documents_uploaded: number;
  documents_limit: number | null;
  // Trade proposals
  trade_proposals_generated: number;
  trade_proposals_limit: number | null;
  // Legal document exports
  legal_documents_generated: number;
  legal_documents_limit: number | null;
  // Support calculations
  support_calculations_used: number;
  support_calculations_limit: number | null;
  // Strategic insights (emotional intelligence, etc.)
  strategic_insights_generated: number;
  strategic_insights_limit: number | null;
  // Sage AI messages
  sage_messages_sent: number;
  sage_messages_limit: number | null;
  // Trial info
  trial_start: string | null;
  trial_end: string | null;
  // Stripe info
  purchased_at: string | null;
  expires_at: string | null;
  stripe_customer_id: string | null;
  amount_paid_cents: number | null;
}

export type ResourcePackType = 'strategy_boost' | 'document_pack' | 'export_pack' | 'full_refill';

export const RESOURCE_PACKS = {
  strategy_boost: { name: 'Strategy Boost', price: 49, adds: '+10 Strategy Regenerations' },
  document_pack: { name: 'Document Pack', price: 49, adds: '+25 Document Uploads' },
  export_pack: { name: 'Export Pack', price: 29, adds: '+10 Legal Document Exports' },
  full_refill: { name: 'Full Refill', price: 99, adds: 'Reset All Usage to Zero' },
};

// Tier defaults for limits
interface TierLimitSet {
  regenerations: number;
  documents: number;
  trade_proposals: number;
  legal_documents: number;
  support_calculations: number;
  strategic_insights: number;
  /** null = unlimited */
  sage_messages: number | null;
}
const TIER_LIMITS: Record<Tier, TierLimitSet> = {
  free: {
    regenerations: 1,
    documents: 3,           // Allow 3 document uploads to demonstrate value
    trade_proposals: 1,     // Allow 1 trade proposal to show AI capabilities
    legal_documents: 0,
    support_calculations: 3,
    strategic_insights: 1,  // Allow 1 Case Intelligence analysis
    sage_messages: 10,      // Allow 10 Sage AI messages
  },
  core: {
    regenerations: 5,
    documents: 10,
    trade_proposals: 5,
    legal_documents: 5,
    support_calculations: 20,
    strategic_insights: 5,
    sage_messages: 100,
  },
  premium: {
    regenerations: 25,
    documents: 50,
    trade_proposals: 25,
    legal_documents: 25,
    support_calculations: 100,
    strategic_insights: 25,
    sage_messages: null, // Unlimited
  },
  professional: {
    regenerations: 50,
    documents: 100,
    trade_proposals: 50,
    legal_documents: 50,
    support_calculations: 200,
    strategic_insights: 50,
    sage_messages: null, // Unlimited
  },
  archive: {
    // Archive tier: read-only, no new generations
    regenerations: 0,
    documents: 0,
    trade_proposals: 0,
    legal_documents: 0,
    support_calculations: 0,
    strategic_insights: 0,
    sage_messages: 0,
  },
};

// Free tier defaults
const FREE_SUBSCRIPTION: Subscription = {
  id: '',
  user_id: '',
  tier: 'free',
  status: 'active',
  features: ['onboarding', 'financial_intake', 'children', 'goals', 'session_notes', 'dashboard'],
  regenerations_used: 0,
  regenerations_limit: TIER_LIMITS.free.regenerations,
  documents_uploaded: 0,
  documents_limit: TIER_LIMITS.free.documents,
  trade_proposals_generated: 0,
  trade_proposals_limit: TIER_LIMITS.free.trade_proposals,
  legal_documents_generated: 0,
  legal_documents_limit: TIER_LIMITS.free.legal_documents,
  support_calculations_used: 0,
  support_calculations_limit: TIER_LIMITS.free.support_calculations,
  strategic_insights_generated: 0,
  strategic_insights_limit: TIER_LIMITS.free.strategic_insights,
  sage_messages_sent: 0,
  sage_messages_limit: TIER_LIMITS.free.sage_messages,
  trial_start: null,
  trial_end: null,
  purchased_at: null,
  expires_at: null,
  stripe_customer_id: null,
  amount_paid_cents: null,
};

// Feature definitions for display purposes
export const FEATURE_INFO: Record<string, { name: string; description: string; requiredTier: Tier }> = {
  full_strategy: { name: 'Full Strategy', description: 'Complete AI-generated divorce strategy', requiredTier: 'core' },
  scenarios_3: { name: '3 Settlement Scenarios', description: 'Three personalized settlement options', requiredTier: 'core' },
  scenarios_5_plus: { name: '5+ Settlement Scenarios', description: 'Five or more detailed settlement scenarios', requiredTier: 'premium' },
  basic_scripts: { name: 'Basic Negotiation Scripts', description: 'Essential talking points for negotiations', requiredTier: 'core' },
  advanced_scripts: { name: 'Advanced Scripts', description: 'Manipulation defense and counter-offer scripts', requiredTier: 'premium' },
  red_flags: { name: 'Red Flag Alerts', description: 'AI detection of concerning patterns', requiredTier: 'premium' },
  manipulation_defense: { name: 'Manipulation Defense', description: 'Tactics to counter psychological manipulation', requiredTier: 'premium' },
  mediation_prep: { name: 'Mediation Prep', description: 'Complete mediation preparation playbook', requiredTier: 'premium' },
  support_calculator: { name: 'Support Calculator', description: 'State-specific child support and alimony calculations', requiredTier: 'core' },
  opponent_basic: { name: 'Basic Opponent Intel', description: 'Personality and communication profiling', requiredTier: 'core' },
  opponent_full: { name: 'Full Opponent Intel', description: 'Leverage, BATNA, and relationship dynamics', requiredTier: 'premium' },
  asset_emotions: { name: 'Asset Emotional Intelligence', description: 'Track emotional value of assets', requiredTier: 'premium' },
  trade_proposals: { name: 'AI Trade Proposals', description: 'AI-generated strategic trade packages', requiredTier: 'premium' },
  session_debrief: { name: 'Session Debrief', description: 'Real-time intelligence updates from sessions', requiredTier: 'premium' },
  business_planning: { name: 'Business Planning', description: 'Post-divorce entity and business planning', requiredTier: 'premium' },
  game_theory_oracle: { name: 'The Oracle', description: 'AI-powered negotiation predictions and settlement optimization', requiredTier: 'premium' },
  correspondence_analysis: { name: 'Case Intelligence', description: 'AI analysis of communications for tactics and red flags', requiredTier: 'premium' },
  export_affidavit: { name: 'Financial Affidavit Export', description: 'Court-ready financial disclosure document', requiredTier: 'core' },
  export_all: { name: 'All Document Exports', description: 'Settlement proposals, cover letters, and more', requiredTier: 'premium' },
  mfa: { name: 'MFA Security', description: 'Multi-factor authentication for account security', requiredTier: 'core' },
  priority_processing: { name: 'Priority AI Processing', description: 'Faster AI response times', requiredTier: 'premium' },
  // Negotiate sub-views
  negotiate_board: { name: 'Negotiation Board', description: 'View and compare settlement positions', requiredTier: 'core' },
  negotiate_redlines: { name: 'Redline Import', description: 'Upload and extract spouse counter-proposal positions', requiredTier: 'core' },
  negotiate_respond: { name: 'Counter-Proposals', description: 'Build and send strategic counter-proposals', requiredTier: 'core' },
  negotiate_playbook: { name: 'Negotiation Playbook', description: 'Tactics, scripts, and talking points for negotiations', requiredTier: 'core' },
  negotiate_optimize: { name: 'Settlement Optimizer', description: 'Game theory analysis and optimal offer calculations', requiredTier: 'premium' },
  negotiate_intel: { name: 'Behavioral Intelligence', description: 'Pattern detection and behavioral analysis of opposing party', requiredTier: 'core' },
};

export function useSubscription() {
  
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: subscription, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async (): Promise<Subscription> => {
      if (!user) return FREE_SUBSCRIPTION;

      const { data, error } = await (supabase as any)
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['active', 'trialing', 'past_due'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching subscription:', error);
        trackSupabaseError('select', 'subscriptions', error);
        return FREE_SUBSCRIPTION;
      }

      if (!data) return FREE_SUBSCRIPTION;

      const tier = data.tier as Tier;
      const tierDefaults = TIER_LIMITS[tier] || TIER_LIMITS.free;

      return {
        id: data.id,
        user_id: data.user_id,
        tier,
        status: data.status as SubscriptionStatus,
        features: (data.features as string[]) || [],
        regenerations_used: data.regenerations_used || 0,
        regenerations_limit: data.regenerations_limit ?? tierDefaults.regenerations,
        documents_uploaded: data.documents_uploaded || 0,
        documents_limit: data.documents_limit ?? tierDefaults.documents,
        trade_proposals_generated: data.trade_proposals_generated || 0,
        trade_proposals_limit: data.trade_proposals_limit ?? tierDefaults.trade_proposals,
        legal_documents_generated: data.legal_documents_generated || 0,
        legal_documents_limit: data.legal_documents_limit ?? tierDefaults.legal_documents,
        support_calculations_used: data.support_calculations_used || 0,
        support_calculations_limit: data.support_calculations_limit ?? tierDefaults.support_calculations,
        strategic_insights_generated: data.strategic_insights_generated || 0,
        strategic_insights_limit: data.strategic_insights_limit ?? tierDefaults.strategic_insights,
        sage_messages_sent: data.sage_messages_sent || 0,
        sage_messages_limit: data.sage_messages_limit ?? tierDefaults.sage_messages,
        trial_start: data.trial_start ?? null,
        trial_end: data.trial_end ?? null,
        purchased_at: data.purchased_at,
        expires_at: data.expires_at,
        stripe_customer_id: data.stripe_customer_id,
        amount_paid_cents: data.amount_paid_cents,
      };
    },
    enabled: !!user,
    staleTime: 1000 * 30, // 30 seconds - keep fresh for feature limits
  });

  // Create checkout session mutation for tiers
  const createCheckout = useMutation({
    mutationFn: async ({
      tier,
      source = 'pricing',
      discountCode,
      recoveryIntentId,
      pricingVariant,
      billingPeriod: billingPeriodOverride,
    }: {
      tier: 'core' | 'premium' | 'upgrade';
      source?: string;
      discountCode?: string;
      recoveryIntentId?: string;
      pricingVariant?: PricingVariant;
      billingPeriod?: 'monthly' | 'annual';
    }) => {
      if (!user) throw new Error('User not authenticated');

      // Get the price for this tier
      const getPrice = (t: string, period?: 'monthly' | 'annual'): number => {
        if (t === 'premium') return 49700;
        if (t === 'upgrade') return 20000;
        if (period === 'annual') return 29000; // Core annual: $290/year
        return 2900; // Core: $29/month
      };

      const billingPeriod = billingPeriodOverride || (tier === 'core' ? 'monthly' : 'one_time');
      const priceCents = getPrice(tier, billingPeriodOverride);

      // Track checkout started for growth metrics
      trackEvent('checkout_started', {
        tier,
        price_cents: priceCents,
        source,
        is_recovery: !!recoveryIntentId,
        pricing_variant: pricingVariant || 'subscription',
        billing_period: billingPeriod,
      });

      // Track to GA4 for conversion funnel
      trackBeginCheckout({
        value: priceCents / 100, // Convert cents to dollars
        plan: tier,
      });

      // Google Ads conversion tracking
      googleAdsConversions.trackBeginCheckout({
        value: priceCents / 100,
      });

      // Also log to engagement events table
      supabase.from('user_engagement_events').insert({
        user_id: user.id,
        event_type: 'checkout_started',
        event_data: {
          tier,
          price_cents: priceCents,
          source,
          is_recovery: !!recoveryIntentId,
          pricing_variant: pricingVariant || 'subscription',
          billing_period: billingPeriod,
          timestamp: new Date().toISOString(),
        },
      });

      // Determine if this is a recovery purchase
      const isRecovery = !!recoveryIntentId;

      // 1. Create checkout intent record BEFORE calling Stripe
      // If this is a recovery, we link to the original intent
      const { data: intent, error: intentError } = await supabase
        .from('checkout_intents')
        .insert({
          user_id: user.id,
          tier,
          billing_period: billingPeriod,
          amount_cents: priceCents,
          source,
          status: 'pending',
          user_agent: navigator.userAgent,
          discount_code: discountCode || null,
          is_recovery_purchase: isRecovery,
          original_intent_id: recoveryIntentId || null,
        })
        .select()
        .single();

      if (intentError) {
        logger.error('Failed to create checkout intent:', intentError);
        // Continue anyway - intent tracking is non-blocking
      }

      // 2. Call edge function with intent ID, discount code, and pricing variant
      const checkoutBody = {
        tier,
        locale: getCurrentLocale(),
        intent_id: intent?.id,
        discount_code: discountCode,
        is_recovery: isRecovery,
        recovery_intent_id: recoveryIntentId,
        pricing_variant: pricingVariant || 'subscription',
        billing_period: billingPeriod,
      };

      let { data, error } = await (supabase as any).functions.invoke('create-checkout-session', {
        body: checkoutBody,
      });

      // Retry once on auth error (token may have expired)
      if (error && (error.message?.includes('Unauthorized') || error.status === 401)) {
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError) {
          const retry = await (supabase as any).functions.invoke('create-checkout-session', {
            body: checkoutBody,
          });
          data = retry.data;
          error = retry.error;
        }
      }

      if (error) throw error;

      // 3. Update intent with Stripe session ID (if we created one)
      if (intent?.id && data.sessionId) {
        await supabase
          .from('checkout_intents')
          .update({ stripe_checkout_session_id: data.sessionId })
          .eq('id', intent.id);
      }

      return data as { sessionId: string; url: string };
    },
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error, variables) => {
      trackStripeError('create_checkout_tier', {
        message: error.message,
        code: (error as any).code,
      });

      // Track checkout abandoned for growth metrics
      trackEvent('checkout_abandoned', {
        tier: variables.tier,
        step: 'create_session',
        error: error.message,
      });

      // Also log to engagement events table if we have user
      if (user) {
        supabase.from('user_engagement_events').insert({
          user_id: user.id,
          event_type: 'checkout_abandoned',
          event_data: {
            tier: variables.tier,
            step: 'create_session',
            error: error.message,
            timestamp: new Date().toISOString(),
          },
        });
      }
    },
  });

  // Create checkout session mutation for resource packs
  const purchaseResourcePack = useMutation({
    mutationFn: async (resourcePack: ResourcePackType) => {
      const checkoutBody = { resource_pack: resourcePack, locale: getCurrentLocale() };

      let { data, error } = await (supabase as any).functions.invoke('create-checkout-session', {
        body: checkoutBody,
      });

      // Retry once on auth error (token may have expired)
      if (error && (error.message?.includes('Unauthorized') || error.status === 401)) {
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError) {
          const retry = await (supabase as any).functions.invoke('create-checkout-session', {
            body: checkoutBody,
          });
          data = retry.data;
          error = retry.error;
        }
      }

      if (error) throw error;
      return data as { sessionId: string; url: string };
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      trackStripeError('create_checkout_resource_pack', {
        message: error.message,
        code: (error as any).code,
      });
    },
  });

  // Helper functions
  const hasFeature = (feature: string): boolean => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return sub.features?.includes(feature) ?? false;
  };

  // Generic usage check helper
  // Note: -1 = unlimited (used for advisor-sponsored clients), null = also unlimited
  const isUnlimited = (limit: number | null): boolean => limit === null || limit === -1;

  const checkLimit = (used: number, limit: number | null): boolean => {
    if (isUnlimited(limit)) return true; // unlimited
    return used < limit;
  };

  const getRemaining = (used: number, limit: number | null): number | null => {
    if (isUnlimited(limit)) return null; // unlimited
    return Math.max(0, limit - used);
  };

  // Strategy regenerations
  const canRegenerate = (): boolean => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return checkLimit(sub.regenerations_used, sub.regenerations_limit);
  };

  const regenerationsRemaining = (): number | null => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return getRemaining(sub.regenerations_used, sub.regenerations_limit);
  };

  // Document uploads
  const canUploadDocument = (): boolean => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return checkLimit(sub.documents_uploaded, sub.documents_limit);
  };

  const documentsRemaining = (): number | null => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return getRemaining(sub.documents_uploaded, sub.documents_limit);
  };

  // Trade proposals
  const canGenerateTradeProposal = (): boolean => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return checkLimit(sub.trade_proposals_generated, sub.trade_proposals_limit);
  };

  const tradeProposalsRemaining = (): number | null => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return getRemaining(sub.trade_proposals_generated, sub.trade_proposals_limit);
  };

  // Legal document exports
  const canGenerateLegalDocument = (): boolean => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return checkLimit(sub.legal_documents_generated, sub.legal_documents_limit);
  };

  const legalDocumentsRemaining = (): number | null => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return getRemaining(sub.legal_documents_generated, sub.legal_documents_limit);
  };

  // Support calculations
  const canCalculateSupport = (): boolean => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return checkLimit(sub.support_calculations_used, sub.support_calculations_limit);
  };

  const supportCalculationsRemaining = (): number | null => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return getRemaining(sub.support_calculations_used, sub.support_calculations_limit);
  };

  // Strategic insights
  const canGenerateStrategicInsight = (): boolean => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return checkLimit(sub.strategic_insights_generated, sub.strategic_insights_limit);
  };

  const strategicInsightsRemaining = (): number | null => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return getRemaining(sub.strategic_insights_generated, sub.strategic_insights_limit);
  };

  // Sage AI messages
  const canSendSageMessage = (): boolean => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return checkLimit(sub.sage_messages_sent, sub.sage_messages_limit);
  };

  const sageMessagesRemaining = (): number | null => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return getRemaining(sub.sage_messages_sent, sub.sage_messages_limit);
  };

  const getRequiredTier = (feature: string): Tier => {
    return FEATURE_INFO[feature]?.requiredTier ?? 'core';
  };

  const tierRank = (tier: Tier): number => {
    // Archive is treated as below free for feature access (read-only)
    const ranks: Record<Tier, number> = { archive: -1, free: 0, core: 1, premium: 2, professional: 3 };
    return ranks[tier];
  };

  const canAccessTier = (requiredTier: Tier): boolean => {
    const currentTier = subscription?.tier ?? 'free';
    return tierRank(currentTier) >= tierRank(requiredTier);
  };

  // Get all usage stats in a structured format
  const getUsageStats = () => {
    const sub = subscription ?? FREE_SUBSCRIPTION;
    return {
      regenerations: { used: sub.regenerations_used, limit: sub.regenerations_limit },
      documents: { used: sub.documents_uploaded, limit: sub.documents_limit },
      tradeProposals: { used: sub.trade_proposals_generated, limit: sub.trade_proposals_limit },
      legalDocuments: { used: sub.legal_documents_generated, limit: sub.legal_documents_limit },
      supportCalculations: { used: sub.support_calculations_used, limit: sub.support_calculations_limit },
      strategicInsights: { used: sub.strategic_insights_generated, limit: sub.strategic_insights_limit },
      sageMessages: { used: sub.sage_messages_sent, limit: sub.sage_messages_limit },
    };
  };

  // Force refetch subscription data - call this after feature usage
  const invalidateSubscription = () => {
    queryClient.invalidateQueries({ queryKey: ['subscription', user?.id] });
  };

  return {
    subscription: subscription ?? FREE_SUBSCRIPTION,
    tier: (subscription?.tier ?? 'free') as Tier,
    isLoading,
    error,
    refetch,

    // Feature access
    hasFeature,
    canRegenerate,
    canUploadDocument,
    canGenerateTradeProposal,
    canGenerateLegalDocument,
    canCalculateSupport,
    canGenerateStrategicInsight,
    canSendSageMessage,
    canAccessTier,
    getRequiredTier,

    // Usage tracking - remaining
    regenerationsRemaining,
    documentsRemaining,
    tradeProposalsRemaining,
    legalDocumentsRemaining,
    supportCalculationsRemaining,
    strategicInsightsRemaining,
    sageMessagesRemaining,

    // Usage tracking - used values
    regenerationsUsed: subscription?.regenerations_used ?? 0,
    documentsUploaded: subscription?.documents_uploaded ?? 0,
    tradeProposalsGenerated: subscription?.trade_proposals_generated ?? 0,
    legalDocumentsGenerated: subscription?.legal_documents_generated ?? 0,
    supportCalculationsUsed: subscription?.support_calculations_used ?? 0,
    strategicInsightsGenerated: subscription?.strategic_insights_generated ?? 0,
    sageMessagesSent: subscription?.sage_messages_sent ?? 0,

    // Get all usage in structured format
    getUsageStats,

    // Tier helpers
    isPremium: (subscription?.tier === 'premium' || subscription?.tier === 'professional'),
    isCore: subscription?.tier === 'core',
    isFree: !subscription || subscription.tier === 'free',
    isPaid: subscription?.tier === 'core' || subscription?.tier === 'premium' || subscription?.tier === 'professional',
    isTrialing: subscription?.status === 'trialing',
    isPastDue: subscription?.status === 'past_due',
    isArchived: subscription?.tier === 'archive',

    // Checkout - tier purchases
    // Wrapper to maintain backward compatibility (accepts tier string or full options)
    createCheckout: (tierOrOptions: 'core' | 'premium' | 'upgrade' | {
      tier: 'core' | 'premium' | 'upgrade';
      source?: string;
      discountCode?: string;
      recoveryIntentId?: string;
      pricingVariant?: PricingVariant;
      billingPeriod?: 'monthly' | 'annual';
    }) => {
      if (typeof tierOrOptions === 'string') {
        createCheckout.mutate({ tier: tierOrOptions });
      } else {
        createCheckout.mutate(tierOrOptions);
      }
    },
    isCreatingCheckout: createCheckout.isPending,
    checkoutError: createCheckout.error,

    // Checkout - resource packs
    purchaseResourcePack: purchaseResourcePack.mutate,
    isPurchasingResourcePack: purchaseResourcePack.isPending,
    resourcePackError: purchaseResourcePack.error,

    // Cache invalidation - call after feature usage to refresh limits
    invalidateSubscription,
  };
}

// Tier pricing for display
export const TIER_PRICING = {
  free: { price: 0, label: 'Free' },
  core: { price: 29, label: '$29/mo', monthly: true },
  premium: { price: 497, label: '$497', monthly: false },
  upgrade: { price: 200, label: '$200', monthly: false }, // Core → Premium one-time upgrade
  professional: { price: 29, label: '$29/mo', monthly: true },
  archive: { price: 10, label: '$10/mo', monthly: true },
};

export const TIER_NAMES: Record<Tier, string> = {
  free: 'Free',
  core: 'Core',
  premium: 'Premium',
  professional: 'Professional',
  archive: 'Archive',
};
